/**
 * Picks For You — orchestrator
 *
 * On the picks-for-you collection page, replaces the server-rendered fallback
 * grid with a personalised set of products derived from behavioural signals.
 *
 * Flow:
 *   1. Collect signals via window.PicksSignals.collect()
 *   2. If signal volume is below threshold, leave server fallback in place
 *   3. Pick top N seeds (orders > wishlist > cart > recently-viewed)
 *   4. Fetch Shopify product recommendations per seed via Section Rendering API
 *      (mirrors snippets/cart-recommendations.liquid:512-549)
 *   5. Merge HTML fragments, dedupe by product id, rank by score, cap to max
 *   6. Replace #picks-grid contents and cache the resulting id list
 *
 * Cache key: intus_picks_cache (sessionStorage). Invalidated when signal hash
 * changes or after 4-hour hard TTL.
 */
(function () {
  'use strict';

  const CACHE_KEY = 'intus_picks_cache';
  const CACHE_TTL_MS = 4 * 60 * 60 * 1000;
  const SIGNAL_THRESHOLD = 2;
  const SEED_COUNT = 5;
  const PER_SEED_LIMIT = 10;
  const SIGNAL_WEIGHTS = { orders: 3, wishlist: 2, cart: 2, recentlyViewed: 1 };
  const INITIAL_LOADING_CLASS = 'picks-initial-loading';

  const root = document.querySelector('[data-picks-for-you]');
  if (!root) return;

  const grid = root.querySelector('[data-picks-grid]');
  const statusEl = root.querySelector('[data-picks-status]');
  const loadingOverlay = root.querySelector('[data-picks-loading]');
  const listSectionId = root.dataset.listSectionId || 'picks-for-you-list';
  const maxProducts = parseInt(root.dataset.maxProducts, 10) || 24;

  function clearInitialLoadingClass() {
    document.documentElement.classList.remove(INITIAL_LOADING_CLASS);
  }

  function showLoading() {
    root.classList.add('picks-for-you--loading');
    if (loadingOverlay) {
      loadingOverlay.setAttribute('aria-hidden', 'false');
    }
    root.setAttribute('aria-busy', 'true');
  }

  function hideLoading() {
    root.classList.remove('picks-for-you--loading');
    if (loadingOverlay) {
      loadingOverlay.setAttribute('aria-hidden', 'true');
    }
    root.removeAttribute('aria-busy');
    clearInitialLoadingClass();
  }

  function setStatus(message) {
    if (!statusEl) return;
    if (message) {
      statusEl.textContent = message;
      statusEl.hidden = false;
    } else {
      statusEl.textContent = '';
      statusEl.hidden = true;
    }
  }

  function readCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.ids) || !parsed.signalsHash || !parsed.ts) return null;
      if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  function writeCache(ids, signalsHash) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ids, signalsHash, ts: Date.now() }));
    } catch {
      /* ignore */
    }
  }

  function pickSeeds(signals) {
    const seen = new Set();
    const seeds = [];
    const sources = [
      { ids: signals.orders, weight: SIGNAL_WEIGHTS.orders },
      { ids: signals.wishlist, weight: SIGNAL_WEIGHTS.wishlist },
      { ids: signals.cart, weight: SIGNAL_WEIGHTS.cart },
      { ids: signals.recentlyViewed, weight: SIGNAL_WEIGHTS.recentlyViewed },
    ];
    for (const source of sources) {
      for (const id of source.ids) {
        if (seeds.length >= SEED_COUNT) break;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        seeds.push({ id, weight: source.weight });
      }
      if (seeds.length >= SEED_COUNT) break;
    }
    return seeds;
  }

  async function fetchSeedRecommendations(seedId) {
    const url = `${window.Shopify.routes.product_recommendations_url}?section_id=${listSectionId}&product_id=${seedId}&limit=${PER_SEED_LIMIT}`;
    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) return [];
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      return Array.from(doc.querySelectorAll('li.grid__item[data-product-id]'));
    } catch (e) {
      console.error('Picks: fetch failed for seed', seedId, e);
      return [];
    }
  }

  function rankAndMerge(seedResults, excludeIds) {
    const exclude = new Set(excludeIds.map(String));
    const scoreById = new Map();
    const elementById = new Map();

    seedResults.forEach(({ items, weight }) => {
      items.forEach((el, rank) => {
        const id = el.dataset.productId;
        if (!id || exclude.has(id)) return;
        const contribution = weight / (rank + 1);
        scoreById.set(id, (scoreById.get(id) || 0) + contribution);
        if (!elementById.has(id)) elementById.set(id, el);
      });
    });

    return Array.from(scoreById.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxProducts)
      .map(([id]) => elementById.get(id));
  }

  async function fetchByProductId(productId) {
    return fetchSeedRecommendations(productId).then((items) => {
      return items.find((el) => el.dataset.productId === String(productId)) || items[0] || null;
    });
  }

  async function renderCachedIds(ids) {
    if (!ids.length) return false;
    const seedId = ids[0];
    const recsItems = await fetchSeedRecommendations(seedId);
    const byId = new Map(recsItems.map((el) => [el.dataset.productId, el]));
    const ordered = [];
    const missingIds = [];
    ids.forEach((id) => {
      if (byId.has(id)) ordered.push(byId.get(id));
      else missingIds.push(id);
    });
    const fetched = await Promise.all(missingIds.slice(0, 8).map((id) => fetchByProductId(id)));
    fetched.forEach((el) => {
      if (el) ordered.push(el);
    });
    if (!ordered.length) return false;
    replaceGrid(ordered);
    return true;
  }

  function replaceGrid(elements) {
    if (!grid || !elements.length) return;
    const frag = document.createDocumentFragment();
    elements.forEach((el) => frag.appendChild(el.cloneNode(true)));
    grid.innerHTML = '';
    grid.appendChild(frag);
    hideLoading();
    grid.dataset.picksRendered = 'personalized';
    document.dispatchEvent(new CustomEvent('picks:rendered', { detail: { count: elements.length } }));
  }

  async function run() {
    showLoading();
    if (!window.PicksSignals) {
      hideLoading();
      return;
    }

    const signals = await window.PicksSignals.collect(root);
    const totalSignals =
      signals.recentlyViewed.length + signals.wishlist.length + signals.cart.length + signals.orders.length;

    if (totalSignals < SIGNAL_THRESHOLD) {
      hideLoading();
      grid.dataset.picksRendered = 'fallback';
      return;
    }

    const signalsHash = window.PicksSignals.hashSignals(signals);
    const cached = readCache();
    if (cached && cached.signalsHash === signalsHash) {
      const ok = await renderCachedIds(cached.ids);
      if (ok) return;
    }

    const seeds = pickSeeds(signals);
    if (!seeds.length) {
      hideLoading();
      grid.dataset.picksRendered = 'fallback';
      return;
    }

    const results = await Promise.allSettled(seeds.map((s) => fetchSeedRecommendations(s.id)));
    const seedResults = results.map((r, i) => ({
      items: r.status === 'fulfilled' ? r.value : [],
      weight: seeds[i].weight,
    }));

    const excludeIds = [
      ...signals.orders,
      ...signals.wishlist,
      ...signals.cart,
      ...signals.recentlyViewed,
    ];
    const ranked = rankAndMerge(seedResults, excludeIds);

    if (!ranked.length) {
      hideLoading();
      grid.dataset.picksRendered = 'fallback';
      return;
    }

    replaceGrid(ranked);
    const ids = ranked.map((el) => el.dataset.productId);
    writeCache(ids, signalsHash);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
